import React, { useCallback, useEffect, useState } from 'react'
import { ResultadoConsulta } from '../../components/RelatorioHtmlFrame'

export interface ErroDaPrevia {
  mensagem: string
  /** Colunas e medidas que a consulta pedia: ajudam a achar o bloco. */
  itens: string[]
  vezes: number
}

const lista = (x: unknown): string[] => (Array.isArray(x) ? x.filter((i): i is string => typeof i === 'string') : [])

/**
 * Guarda as consultas da prévia que falharam, para mostrar todas juntas em vez de cada uma enterrada no bloco dela (que só se
 * vê rolando a prévia). A lista recomeça quando a prévia é refeita (<paramref name="chave" /> muda).
 */
export function useErrosDaPrevia(consultar: (consulta: unknown) => Promise<ResultadoConsulta>, chave: unknown) {
  const [erros, setErros] = useState<ErroDaPrevia[]>([])
  useEffect(() => { setErros([]) }, [chave])

  const comRegistro = useCallback(async (consulta: unknown): Promise<ResultadoConsulta> => {
    try {
      return await consultar(consulta)
    } catch (e: any) {
      const c = (consulta ?? {}) as { colunas?: unknown; medidas?: unknown }
      const mensagem = String(e?.message || 'Falha ao consultar os dados')
      const itens = [...lista(c.colunas), ...lista(c.medidas)]
      setErros(atuais => {
        const i = atuais.findIndex(x => x.mensagem === mensagem)
        if (i < 0) return [...atuais, { mensagem, itens, vezes: 1 }]
        const novos = [...atuais]
        novos[i] = { ...novos[i], itens: Array.from(new Set([...novos[i].itens, ...itens])), vezes: novos[i].vezes + 1 }
        return novos
      })
      throw e
    }
  }, [consultar])

  return { consultar: comRegistro, erros }
}

/** Os erros da prévia, no topo dela, com o motivo por extenso. */
export default function ErrosDaPrevia({ erros }: { erros: ErroDaPrevia[] }) {
  if (erros.length === 0) return null
  return (
    <div className="rh-alerta erro" role="alert" data-testid="erros-da-previa">
      <b>{erros.length === 1 ? '1 consulta da prévia falhou' : `${erros.length} tipos de erro nas consultas da prévia`}:</b>
      <ul>
        {erros.map(e => (
          <li key={e.mensagem}>
            {e.mensagem}
            {e.itens.length > 0 && <small style={{ display: 'block', opacity: 0.8 }}>Pedia: {e.itens.slice(0, 6).join(', ')}{e.itens.length > 6 ? '…' : ''}</small>}
            {e.vezes > 1 && <small style={{ display: 'block', opacity: 0.8 }}>{e.vezes} consultas</small>}
          </li>
        ))}
      </ul>
    </div>
  )
}
